import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import logger from '@/utils/logger';
import {
  RefreshCw, CheckCircle2, XCircle, AlertTriangle, Clock,
  ChevronLeft, ChevronRight, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  completed: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', label: 'Completed' },
  failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Failed' },
  partial: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Partial' },
  running: { icon: RefreshCw, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Running' },
};

export default function MetaAdsSyncLogs({ onBack }) {
  const { getAuthHeaders } = useAdmin();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const headers = getAuthHeaders();

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const hdrs = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/meta-ads/sync-logs?page=${page}&limit=20`, { headers: hdrs });
      const data = await res.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (e) { logger.error(e); }
    finally { setLoading(false); }
  }, [page, getAuthHeaders]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-4" data-testid="sync-logs-page">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-slate-500 hover:text-blue-600">
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-xl font-bold text-slate-900">Sync History</h2>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="p-3 text-left font-medium text-slate-600">Date/Time</th>
              <th className="p-3 text-left font-medium text-slate-600">Type</th>
              <th className="p-3 text-left font-medium text-slate-600">Triggered By</th>
              <th className="p-3 text-left font-medium text-slate-600">Status</th>
              <th className="p-3 text-left font-medium text-slate-600">New Leads</th>
              <th className="p-3 text-left font-medium text-slate-600">Duplicates</th>
              <th className="p-3 text-left font-medium text-slate-600">Error</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">
                <RefreshCw className="animate-spin inline mr-2" size={16} /> Loading...
              </td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">No sync history yet</td></tr>
            ) : logs.map(log => {
              const st = STATUS_STYLES[log.status] || STATUS_STYLES.completed;
              const Icon = st.icon;
              return (
                <tr key={log.id} className="border-b hover:bg-slate-50">
                  <td className="p-3 whitespace-nowrap text-slate-600">
                    {log.started_at ? new Date(log.started_at).toLocaleString() : '—'}
                  </td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium capitalize">
                      {log.sync_type}
                    </span>
                  </td>
                  <td className="p-3 text-slate-600">{log.triggered_by_name || 'System'}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${st.bg} ${st.color}`}>
                      <Icon size={12} /> {st.label}
                    </span>
                  </td>
                  <td className="p-3 font-medium text-green-600">{log.leads_new}</td>
                  <td className="p-3 text-slate-500">{log.leads_duplicate}</td>
                  <td className="p-3 text-red-500 text-xs max-w-[200px] truncate">{log.error_message || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {total > 20 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
            <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft size={16} />
              </Button>
              <Button size="sm" variant="ghost" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
